import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { useT, switchPath } from "@/lib/i18n";
import { useSite } from "@/context/SiteContext";
import { waLink } from "@/lib/api";

export function WhatsAppButton({ text, className = "btn-line", label, testid = "whatsapp-link" }) {
  const { settings } = useSite();
  const { t } = useT();
  return (
    <a href={waLink(settings?.whatsapp_number || "351916230822", text || t.wa_default)} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}>
      <MessageCircle className="w-4 h-4" strokeWidth={1.4} /> {label ?? t.whatsapp}
    </a>
  );
}

export function Header() {
  const { t, lang, r } = useT();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const links = [[r.catalog, t.nav_catalog, "nav-catalog"], [r.orders, t.nav_orders, "nav-orders"], [r.contact, t.nav_contact, "nav-contact"]];
  const navCls = ({ isActive }) => `text-[13px] tracking-[0.18em] uppercase transition-colors duration-300 ${isActive ? "text-[#E6D5AC]" : "text-[#F3EFE6]/70 hover:text-[#E6D5AC]"}`;
  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0C0B09]/80 border-b border-[#C8AE6E]/10" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 h-[72px] flex items-center justify-between gap-6">
        <Link to={r.home} className="flex items-center gap-3 group" data-testid="header-home-link" onClick={() => setOpen(false)}>
          <img src="/emblem-square.png" alt="" className="w-10 h-10 rounded-full ring-1 ring-[#C8AE6E]/30 group-hover:ring-[#C8AE6E]/70 transition-[box-shadow] duration-500" />
          <span className="font-display text-lg tracking-[0.2em] text-[#F3EFE6]">GOLDENTIME</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-10">
          {links.map(([to, l, id]) => <NavLink key={to} to={to} className={navCls} data-testid={id}>{l}</NavLink>)}
        </nav>
        <div className="flex items-center gap-3">
          <div className="flex items-center text-xs tracking-[0.2em]" data-testid="lang-switcher">
            {["pt", "en"].map((l, i) => (
              <span key={l} className="flex items-center">
                {i > 0 && <span className="mx-1.5 text-[#F3EFE6]/30">/</span>}
                <Link to={l === lang ? pathname + search : switchPath(pathname, search, l)} data-testid={`lang-${l}`} className={l === lang ? "text-[#C8AE6E]" : "text-[#F3EFE6]/50 hover:text-[#F3EFE6]"}>{l.toUpperCase()}</Link>
              </span>
            ))}
          </div>
          <WhatsAppButton className="hidden sm:inline-flex btn-line !px-4 !py-2 text-xs" testid="header-whatsapp-button" />
          <button className="lg:hidden -mr-2 p-3 text-[#F3EFE6]" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#C8AE6E]/10 bg-[#0C0B09] px-6 py-6 flex flex-col gap-5 text-base" data-testid="mobile-menu">
          {links.map(([to, l, id]) => <NavLink key={to} to={to} className={({ isActive }) => `block py-2 text-[15px] tracking-[0.18em] uppercase ${isActive ? "text-[#E6D5AC]" : "text-[#F3EFE6]/80"}`} onClick={() => setOpen(false)} data-testid={`mobile-${id}`}>{l}</NavLink>)}
          <WhatsAppButton className="btn-line self-start" testid="mobile-whatsapp-button" />
        </nav>
      )}
    </header>
  );
}

export function Footer() {
  const { t, r } = useT();
  return (
    <footer className="border-t border-[#C8AE6E]/10 mt-32" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <img src="/emblem-square.png" alt="" className="w-12 h-12 rounded-full" />
            <span className="font-display tracking-[0.2em] text-[#F3EFE6]">GOLDENTIME</span>
          </div>
          <p className="mt-5 text-sm text-[#A39E93] max-w-xs">{t.footer_tag}</p>
        </div>
        <div className="space-y-3 text-sm">
          <p className="eyebrow mb-4">{t.nav_contact}</p>
          <Link to={r.track} className="block text-[#F3EFE6]/80 hover:text-[#E6D5AC]" data-testid="footer-track">{t.nav_track}</Link>
          <WhatsAppButton className="inline-flex items-center gap-2 text-[#F3EFE6]/80 hover:text-[#E6D5AC]" label="+351 916 230 822" testid="footer-whatsapp" />
        </div>
        <div className="space-y-3 text-sm flex flex-col">
          <p className="eyebrow mb-4">Legal</p>
          <Link to={r.privacy} className="text-[#F3EFE6]/80 hover:text-[#E6D5AC]" data-testid="footer-privacy">{t.privacy}</Link>
          <Link to={r.terms} className="text-[#F3EFE6]/80 hover:text-[#E6D5AC]" data-testid="footer-terms">{t.terms}</Link>
        </div>
      </div>
      <div className="border-t border-[#C8AE6E]/10 py-6 text-center text-[11px] tracking-[0.2em] text-[#A39E93]/70">© {new Date().getFullYear()} GOLDENTIME · GOLDENTIME.CLUB</div>
    </footer>
  );
}
